import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Cabecera de una pagina: el titulo, una descripcion opcional y las acciones.
 *
 * Existe porque 22 ficheros escribian la suya con su propio `<h1>`, y entre las
 * paginas habia cinco tamanos de titulo distintos. Mientras cada pantalla
 * escriba la suya, habra tantas versiones como pantallas.
 *
 * **No es una caja.** El criterio de composicion
 * (harness/skills/muveran-design/SKILL.md) da dos niveles de contenedor visible,
 * y una seccion se hace con espacio y un titulo, no con un borde: la cabecera es
 * nivel 1. Varias paginas la envolvian en `rounded-lg border bg-card`, y al
 * extraerla se corrige.
 *
 *   <PageHeader title="Herramientas" description="...">
 *     <Button onClick={crear}>Crear</Button>
 *   </PageHeader>
 *
 * Los hijos son las acciones: se alinean a la derecha y saltan de linea antes
 * que recortarse.
 */
interface PageHeaderProps
  extends Omit<React.ComponentProps<"header">, "title"> {
  title: React.ReactNode
  description?: React.ReactNode
}

const PageHeader = React.forwardRef<HTMLElement, PageHeaderProps>(
  ({ className, title, description, children, ...props }, ref) => (
    <header
      ref={ref}
      className={cn(
        "flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between",
        className,
      )}
      {...props}
    >
      <div className="min-w-0 space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">{children}</div>
      ) : null}
    </header>
  ),
)
PageHeader.displayName = "PageHeader"

export { PageHeader }
